// src/xmlImport_Export.ts
import { DataModel, defaultData } from "./AppTypes";
import { sanitizeXmlName, xmlFromData, xmlToData } from "./xmlHandling";

/**
 * Öffnet einen File-Picker, liest XML ein, wandelt es in ein vollständiges DataModel
 * (inkl. conditionGroups/rules_xml/plan_csv) und ruft onSuccess(data).
 *
 * Du kannst dann in deinem Code z.B. machen:
 *
 *   importRulesXml((data) => {
 *     setData(data);
 *     onRulesChange(data.rules_xml);
 *   });
 */
export function importRulesXml(
  onSuccess: (data: DataModel) => void,
  onError?: (msg: string) => void
) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xml";
  input.onchange = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = (ev.target?.result ?? "") as string;
      if (!text) {
        onError?.("Import failed: empty file");
        return;
      }

      const parsed = xmlToData(text);
      if (!parsed) {
        onError?.("Import failed: invalid XML");
        return;
      }

      // Vollständiges DataModel bauen, wie du es in Administration.tsx benutzt
      const full: DataModel = {
        settings: parsed.settings,
        dimensions: parsed.dimensions,
        timings: parsed.timings ?? [],
        relations: parsed.relations,
        creations: parsed.creations ?? defaultData.creations,
        capacities: parsed.capacities,
        conditions: parsed.conditions,
        conditionGroups: parsed.conditionGroups || defaultData.conditionGroups,
        metrics: parsed.metrics ?? [],
        objectives: parsed.objectives ?? [],
        fixations: parsed.fixations ?? [],
        adjustments: parsed.adjustments ?? [],
        // XML aus Datei speichern
        rules_xml: text,
        // Plan ist beim reinen Rules-Import erstmal leer
        plan_csv: parsed.plan_csv ?? "",
        fulfillments_json: null,
        syntaxchecks_json: null,
      };

      onSuccess(full);
    };
    reader.readAsText(file);
  };
  input.click();
}

/** Dateiname nach Konvention: <settings.name>_<settings.modified>.xml */
export function getExportFilename(name: string): string {
  const safeName = sanitizeXmlName(name)
    .replace(/[\\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");
  const safeTime = new Date().toISOString().replace(/:/g, "-");
  return `${safeName}_${safeTime}.xml`;
}

/**
 * Aktuelles rules_xml als Download anbieten.
 *
 * Wenn data.rules_xml leer ist, wird aus dem DataModel neu XML gebaut.
 */
export function exportRulesXml(data: DataModel, name: string) {
  // Immer neu generieren, damit aktuelle Änderungen enthalten sind
  const xml = xmlFromData(data);
  const blob = new Blob([xml], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = getExportFilename(name).replace(/\.xml$/, ".xml");
  a.click();
  URL.revokeObjectURL(url);
}
